import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Typography, Paper, Grid, CircularProgress, Alert, FormControlLabel, Switch } from '@mui/material';
import { useAuth } from '../context/AuthContext'; // Corrected: Import useAuth hook
import { api } from '../utils/api'; // Assuming your api utility is set up for authenticated calls

const UserSettingsPage = () => {
  const { user } = useAuth();
  const [jiraSettings, setJiraSettings] = useState({
    jiraUrl: '',
    jiraUsername: '',
    jiraApiToken: '', // This will be write-only to the backend, backend GET won't return it
    isJiraApiTokenSet: false, // From backend GET to indicate if token is already saved
    jiraStoryPointFieldId: '',
    jiraProjectKey: ''
  });
  const [profileSettings, setProfileSettings] = useState({
    name: user?.name || '',
    // email: user?.email || '' // Email might not be editable
  });
  const [userPreferences, setUserPreferences] = useState({
    autoOpenJiraImport: true,
    githubLogin: '',
  });
  const [githubLoginDraft, setGithubLoginDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchJiraSettings = async () => {
      setLoading(true);
      try {
        const response = await api.get('/users/me/settings/jira');
        setJiraSettings(prev => ({
          ...prev,
          jiraUrl: response.data.jiraUrl || '',
          jiraUsername: response.data.jiraUsername || '',
          isJiraApiTokenSet: response.data.isJiraApiTokenSet || false,
          jiraStoryPointFieldId: response.data.jiraStoryPointFieldId || '',
          jiraProjectKey: response.data.jiraProjectKey || ''
        }));
        setError('');
      } catch (err) {
        console.error('Failed to fetch JIRA settings', err);
        setError(err.response?.data?.msg || err.message || 'Failed to fetch JIRA settings');
      }
      setLoading(false);
    };

    const fetchUserPreferences = async () => {
      try {
        const response = await api.get('/users/me/settings/preferences');
        setUserPreferences({
          autoOpenJiraImport: response.data.autoOpenJiraImport !== false,
          githubLogin: response.data.githubLogin || '',
        });
        setGithubLoginDraft(response.data.githubLogin || '');
      } catch (err) {
        console.error('Failed to fetch user preferences', err);
      }
    };

    if (user) {
      fetchJiraSettings();
      fetchUserPreferences();
      setProfileSettings({ name: user.name });
    }
  }, [user]);

  const handleJiraChange = (e) => {
    setJiraSettings({ ...jiraSettings, [e.target.name]: e.target.value });
  };

  const handleProfileChange = (e) => {
    setProfileSettings({ ...profileSettings, [e.target.name]: e.target.value });
  };

  const handlePreferenceChange = async (prefName, value) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = { [prefName]: value };
      const response = await api.put('/users/me/settings/preferences', payload);
      setUserPreferences((prev) => ({
        ...prev,
        autoOpenJiraImport: response.data.autoOpenJiraImport !== false,
        githubLogin: response.data.githubLogin || '',
      }));
      setSuccess('Preferences updated successfully!');
    } catch (err) {
      console.error('Failed to update preferences', err);
      setError(err.response?.data?.msg || err.message || 'Failed to update preferences');
    }
    setLoading(false);
  };

  const handleJiraSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        jiraUrl: jiraSettings.jiraUrl,
        jiraUsername: jiraSettings.jiraUsername,
        jiraStoryPointFieldId: jiraSettings.jiraStoryPointFieldId,
        jiraProjectKey: jiraSettings.jiraProjectKey,
      };
      // Only include the API token if the user has entered something new
      // or if they want to clear it by submitting an empty string
      if (jiraSettings.jiraApiToken || jiraSettings.jiraApiToken === '') {
        payload.jiraApiToken = jiraSettings.jiraApiToken;
      }

      const response = await api.put('/users/me/settings/jira', payload);
      setJiraSettings(prev => ({
        ...prev,
        jiraUrl: response.data.jiraUrl || '',
        jiraUsername: response.data.jiraUsername || '',
        isJiraApiTokenSet: response.data.isJiraApiTokenSet || false,
        jiraStoryPointFieldId: response.data.jiraStoryPointFieldId || '',
        jiraProjectKey: response.data.jiraProjectKey || '',
        jiraApiToken: '' // Clear the input field after successful save
      }));
      setSuccess('JIRA settings updated successfully!');
    } catch (err) {
      console.error('Failed to update JIRA settings', err);
      setError(err.response?.data?.msg || err.response?.data?.errors?.map(e => e.msg).join(', ') || err.message || 'Failed to update JIRA settings');
    }
    setLoading(false);
  };

  // Placeholder for profile submit
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    // TODO: Implement profile update logic if User model & API supports it
    setLoading(true);
    setError('');
    setSuccess('');
    try {
        // Example: await api.put('/users/me/profile', { name: profileSettings.name });
        setSuccess('Profile updated successfully! (Frontend Only - No API for now)');
        // Re-fetch user from AuthContext or update AuthContext if name changes
    } catch (err) {
        setError('Failed to update profile.');
    }
    setLoading(false);
  };

  if (!user) {
    return <Typography>Please log in to view settings.</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        User Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Profile
        </Typography>
        <form onSubmit={handleProfileSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={profileSettings.name}
                onChange={handleProfileChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Read-only)"
                name="email"
                value={user.email || ''}
                disabled
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1}}>
              <Button type="submit" variant="contained" color="primary" disabled={loading || !profileSettings.name}>
                {loading ? <CircularProgress size={24} /> : 'Save Profile'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Session Preferences
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={userPreferences.autoOpenJiraImport}
              onChange={(e) => handlePreferenceChange('autoOpenJiraImport', e.target.checked)}
              name="autoOpenJiraImport"
              color="primary"
              disabled={loading}
            />
          }
          label="Auto-open JIRA import in empty sessions"
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          When enabled, the JIRA import dialog will automatically open when you enter an empty session with JIRA configured.
        </Typography>

        <Grid container spacing={1} alignItems="flex-start" sx={{ mt: 2 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="GitHub username"
              name="githubLogin"
              value={githubLoginDraft}
              onChange={(e) => setGithubLoginDraft(e.target.value)}
              placeholder="octocat"
              disabled={loading}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={() => handlePreferenceChange('githubLogin', githubLoginDraft.trim())}
              disabled={loading || githubLoginDraft.trim() === (userPreferences.githubLogin || '')}
              sx={{ mt: 0.5 }}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Used to match you to your own pull requests in a future "My Work" view. Optional.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          JIRA Connection Settings
        </Typography>
        <form onSubmit={handleJiraSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="JIRA URL"
                name="jiraUrl"
                value={jiraSettings.jiraUrl}
                onChange={handleJiraChange}
                placeholder="https://your-domain.atlassian.net"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="JIRA Username (Email)"
                name="jiraUsername"
                value={jiraSettings.jiraUsername}
                onChange={handleJiraChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label={jiraSettings.isJiraApiTokenSet ? "JIRA API Token (Saved - Enter to change)" : "JIRA API Token"}
                name="jiraApiToken"
                value={jiraSettings.jiraApiToken} // This will be empty unless user types a new one
                onChange={handleJiraChange}
                helperText={jiraSettings.isJiraApiTokenSet ? "Leave blank to keep the currently saved token." : "Your JIRA API token for authentication."}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Default JIRA Project Key"
                name="jiraProjectKey"
                value={jiraSettings.jiraProjectKey}
                onChange={handleJiraChange}
                helperText="(Optional) Default project key for searching issues."
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Default Story Point Field ID"
                name="jiraStoryPointFieldId"
                value={jiraSettings.jiraStoryPointFieldId}
                onChange={handleJiraChange}
                helperText="(Optional) e.g., customfield_10016. For pushing estimates."
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1}}>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Save JIRA Settings'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default UserSettingsPage; 